import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { App } from 'antd';
import React, { useEffect } from 'react';
import {
  AvatarDropdown,
  AvatarName,
  HospitalName,
  NoticeAction,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import type { API } from '@/services/ant-design-pro/typings';
import { getDefaultDashboardPath, isRouteValidForUser } from '@/utils/route';
import defaultSettings from '../config/defaultSettings';
import { errorConfig, initGlobalMessageInstances } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

// 用于跟踪上一次的路径
let previousPath = '';

// 自定义图标组件 - 根据路径和选中状态显示不同图标
const KnowledgeMenuIcon: React.FC<{ selected?: boolean }> = ({ selected }) => {
  const [isSelected, setIsSelected] = React.useState(selected || false);

  React.useEffect(() => {
    // 监听路由变化
    const checkPath = () => {
      const currentPath = history.location.pathname;
      setIsSelected(currentPath === '/knowledge');
    };

    // 初始化
    checkPath();

    // 监听路由变化
    const unlisten = history.listen(() => {
      checkPath();
    });

    return () => {
      unlisten();
    };
  }, []);

  return (
    <img
      src={
        isSelected ? '/icons/bookmenuselecticon.png' : '/icons/bookmenuicon.png'
      }
      alt="knowledge icon"
      style={{ width: '16px', height: '16px' }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      // 后端返回格式：{ status: 'success', data: {...}, message: '...' }
      if (msg.status === 'success' && msg.data) {
        // 转换后端字段到前端需要的格式
        return {
          ...msg.data,
          name: msg.data.full_name || msg.data.name,
          userid: msg.data.id || msg.data.userid,
          // 使用 primary_role 来判断用户身份，如果没有则回退到 user_type
          access: msg.data.primary_role || msg.data.user_type,
        };
      }
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面和邀请页面，执行
  const { location } = history;
  const isInvitationPage = location.pathname.startsWith('/invitation');
  if (location.pathname !== loginPath && !isInvitationPage) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    headerTitleRender: (logo, title) => {
      // 获取 commit hash 前8位
      const commitHash = process.env.GIT_COMMIT_HASH || '';
      const shortHash = commitHash.substring(0, 8);

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
          <div style={{ cursor: 'pointer' }}>{logo}</div>
          <div style={{ cursor: 'pointer' }}>{title}</div>
          {shortHash && (
            <span
              style={{
                color: '#eee',
                fontSize: '12px',
                marginLeft: '8px',
                fontWeight: 'normal',
              }}
            >
              {shortHash}
            </span>
          )}
          <HospitalName key="hospital" />
        </div>
      );
    },
    menu: {
      // 使用 params 函数传递用户信息作为依赖，当用户信息变化时菜单会自动重新计算
      // 当 initialState.currentUser 变化时，params 函数会返回新的值，触发菜单重新计算
      params: () => {
        const userId =
          initialState?.currentUser?.userid || initialState?.currentUser?.id;
        const userRole =
          initialState?.currentUser?.access ||
          initialState?.currentUser?.primary_role;
        return {
          userId: userId || '',
          userRole: userRole || '',
          // 添加当前路径以便动态生成菜单链接
          currentPath: history.location.pathname,
          // 使用组合 key 来确保当用户信息变化时能够触发菜单更新
          menuKey: `${userId || 'anonymous'}-${userRole || 'none'}`,
        };
      },
      request: async (params, defaultMenuData) => {
        // 判断用户是否是项目管理员（根据 primary_role）
        const isProjectAdmin =
          initialState?.currentUser?.access === 'project_admin' ||
          initialState?.currentUser?.primary_role === 'project_admin';

        // 过滤菜单数据
        return defaultMenuData
          .filter((item) => {
            // 只有项目管理员可以看到患者管理菜单
            if (item.path === '/patient') {
              return isProjectAdmin;
            }
            return true;
          })
          .map((item) => {
            // 为 knowledge 路由添加自定义图标
            if (item.path === '/knowledge') {
              return {
                ...item,
                icon: <KnowledgeMenuIcon />,
              };
            }
            // 为患者管理菜单保持原路径（患者列表是跨项目聚合的，不需要 projectId）
            if (item.path === '/patient' && isProjectAdmin) {
              return item;
            }
            return item;
          });
      },
    },
    actionsRender: () => [
      // <Question key="doc" />,
      <div key="notice" style={{ marginLeft: '16px' }}>
        <NoticeAction />
      </div>,
      // <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src:
        initialState?.currentUser?.avatar ||
        'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png',
      title: <AvatarName />,
      size: 70,
      style: {
        width: '32px',
        height: '32px',
      },
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      const currentPath = location.pathname;
      const isInvitationPage = currentPath.startsWith('/invitation');

      // 如果没有登录，重定向到 login（排除登录页和邀请页）
      if (
        !initialState?.currentUser &&
        currentPath !== loginPath &&
        !isInvitationPage
      ) {
        history.push(loginPath);
        return;
      }

      // 如果已登录，验证当前路径是否适合当前用户类型
      if (
        initialState?.currentUser &&
        currentPath !== loginPath &&
        !isInvitationPage
      ) {
        // 验证路由是否适合当前用户
        if (!isRouteValidForUser(currentPath, initialState.currentUser)) {
          // 路由不适合当前用户，重定向到默认的 dashboard 路径
          const defaultPath = getDefaultDashboardPath(initialState.currentUser);
          if (currentPath !== defaultPath) {
            history.replace(defaultPath);
          }
        }
      }

      // 患者管理页面特殊处理：每次从其他页面进入时自动收起侧边栏
      if (currentPath === '/patient' && previousPath !== '/patient') {
        // 使用 setTimeout 确保在 layout 完全渲染后执行
        setTimeout(() => {
          setInitialState((prev) => ({
            ...prev,
            settings: {
              ...prev?.settings,
              collapsed: true,
            },
          }));
        }, 100);
      }

      // 更新上一次的路径
      previousPath = currentPath;
    },
    // 处理侧边栏展开/收起事件
    onCollapse: (collapsed: boolean) => {
      setInitialState((prev) => ({
        ...prev,
        settings: {
          ...prev?.settings,
          collapsed,
        },
      }));
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      // 初始化全局 message 和 notification 实例的组件
      const AppWrapper: React.FC<{ children: React.ReactNode }> = ({
        children: appChildren,
      }) => {
        const { message, notification } = App.useApp();

        useEffect(() => {
          // 初始化全局实例
          initGlobalMessageInstances(message, notification);
        }, [message, notification]);

        return <>{appChildren}</>;
      };

      return (
        <App>
          <AppWrapper>
            {children}
            {isDev && (
              <SettingDrawer
                disableUrlParams
                enableDarkTheme
                settings={initialState?.settings}
                onSettingChange={(settings) => {
                  setInitialState((preInitialState) => ({
                    ...preInitialState,
                    settings,
                  }));
                }}
              />
            )}
          </AppWrapper>
        </App>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 设置正式API基础地址，不走proxy
  // // baseURL: 'https://api.tlc-staging.benaimed.com', // 预发布环境
  // baseURL: 'http://localhost:18300', // 本地环境
  baseURL: 'https://api.tlc-testing.benaimed.com', // 测试环境
  // baseURL: '', // mock环境
  ...errorConfig,
};
